import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Label,
  MenuToggle,
  type MenuToggleElement,
  Pagination,
  Popover,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ArchiveIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  OutlinedClockIcon,
  PlusCircleIcon,
  SearchIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { newsletterToSummary, useNewsletters } from '../../../hooks/useNewsletters';
import { type Newsletter, type NewsletterStatus, type NewsletterSummary, newsletterStatusLabel } from '../../../types/models/Newsletter';
import type { ApiResponseNewsletterSingle } from '../../../types/api/ApiResponseNewsletter';
import { formatDateNumeric, formatDateTimeNumeric } from '../../../utils/dateFormat';
import { formatBytes } from '../../../utils/formatBytes';
import NewsletterRowActions from './NewsletterRowActions';
import NewsletterEditorModal from './NewsletterEditorModal';
import NewsletterDeleteModal from './NewsletterDeleteModal';
import NewsletterSendModal from './NewsletterSendModal';
import NewsletterPreviewPopoverBody from './NewsletterPreviewPopoverBody';

/** Which server-side set the list shows: active newsletters or the archived (soft-deleted) set. */
type ArchivedFilter = 'active' | 'archived';

const ARCHIVED_FILTER_LABEL: Record<ArchivedFilter, string> = { active: 'Active', archived: 'Archived' };

const isArchivedFilter = (v: unknown): v is ArchivedFilter => v === 'active' || v === 'archived';

type LabelColor = React.ComponentProps<typeof Label>['color'];

// Status → label colour. The text (from newsletterStatusLabel) always conveys the status, so colour
// is reinforcement only — no icon needed for accessibility.
const STATUS_COLOR: Record<NewsletterStatus, LabelColor> = {
  draft: 'grey',
  queued: 'blue',
  sending: 'blue',
  sent: 'green',
  failed: 'red',
};

/**
 * Status cell: the lifecycle label, plus — for a newsletter queued with a FUTURE `not_before`
 * (`is_scheduled`) — a distinct "Scheduled" badge and its compact send time. The clock icon (not
 * colour alone) carries the meaning for accessibility; the visible time avoids a discouraged
 * static-label tooltip.
 */
const renderStatus = (newsletter: NewsletterSummary) => {
  const color = STATUS_COLOR[newsletter.status as NewsletterStatus] ?? 'grey';
  return (
    <Flex spaceItems={{ default: 'spaceItemsXs' }} alignItems={{ default: 'alignItemsCenter' }} flexWrap={{ default: 'wrap' }}>
      <FlexItem>
        <Label isCompact color={color}>
          {newsletterStatusLabel(newsletter.status)}
        </Label>
      </FlexItem>
      {newsletter.is_scheduled && (
        <>
          <FlexItem>
            <Label isCompact color="orange" icon={<OutlinedClockIcon />}>
              Scheduled
            </Label>
          </FlexItem>
          {newsletter.not_before !== null && (
            <FlexItem>
              <Content component="small">{formatDateTimeNumeric(newsletter.not_before)}</Content>
            </FlexItem>
          )}
        </>
      )}
    </Flex>
  );
};

/**
 * NewslettersTab — staff CRUD + lifecycle over newsletters (each built on a template), living in the
 * "Newsletters" tab of the Ancalagon-staff-only Newsletter admin page.
 *
 * The Active/Archived selector chooses the server-side set (`?archived=1`); search over the subject
 * and pagination are client-side over the fetched set. Row kebab: Modify/Send (drafts), Preview (a
 * proof to any address, via a popover), and Delete/Archive (the backend's delete state machine). It
 * fetches its own data, so the parent `<Tabs>` uses `mountOnEnter` and this tab exposes Refresh.
 */
function NewslettersTab() {
  const [archivedFilter, setArchivedFilter] = useState<ArchivedFilter>('active');
  const isArchivedView = archivedFilter === 'archived';

  const { newsletters, loading, error, refetch, applyNewsletter, removeNewsletter } = useNewsletters(isArchivedView);
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<NewsletterSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NewsletterSummary | null>(null);
  const [sendTarget, setSendTarget] = useState<NewsletterSummary | null>(null);
  const [previewTarget, setPreviewTarget] = useState<NewsletterSummary | null>(null);
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filteredNewsletters = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return newsletters;
    }
    return newsletters.filter((n) => n.subject.toLowerCase().includes(q));
  }, [newsletters, debouncedSearch]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredNewsletters.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredNewsletters.length, perPage, page]);

  const paginatedNewsletters = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredNewsletters.slice(start, start + perPage);
  }, [filteredNewsletters, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => setPage(pageNumber);
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPage(1);
  };

  const selectView = (next: ArchivedFilter) => {
    setArchivedFilter(next);
    setPage(1);
    setIsFilterOpen(false);
  };

  const closePreview = () => {
    setPreviewTarget(null);
    setPreviewAnchor(null);
  };

  // A create/edit save: upsert the row; if the user CREATED (always an active draft) while viewing
  // the Archived set, switch to Active so their new newsletter is visible.
  const handleSaved = (saved: Newsletter) => {
    const wasCreate = editTarget === null;
    applyNewsletter(saved);
    if (wasCreate && isArchivedView) {
      selectView('active');
    }
  };

  const openPreview = (newsletter: NewsletterSummary, anchor: HTMLElement | null) => {
    setPreviewAnchor(anchor);
    setPreviewTarget(newsletter);
  };

  // Clone a newsletter (ANY status) into a fresh draft, refresh the list, then open the editor on the
  // clone — same UX as Modify. A clone is always an ACTIVE draft, so if the Archived view is showing,
  // switch to Active (which refetches that set) so the new row is visible behind the modal. The ref
  // guards against a second click while the POST is still in flight (would create a second clone).
  const duplicatingRef = useRef(false);
  const handleDuplicate = async (source: NewsletterSummary) => {
    if (duplicatingRef.current) {
      return;
    }
    duplicatingRef.current = true;
    try {
      const response = await apiFetch(`/admin/newsletters/${source.id}/duplicate`, { method: 'POST' });
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterSingle | null;

      if (body?.status === 'success' && body.data?.newsletter) {
        const created = body.data.newsletter;
        if (isArchivedView) {
          selectView('active');
        } else {
          await refetch();
        }
        setEditTarget(newsletterToSummary(created));
        addNotification({
          title: 'Newsletter duplicated',
          srTitle: 'Success alert',
          description: `${created.subject} was created as a draft — opening it for editing.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return;
      }

      const message = body?.message ?? 'Could not duplicate the newsletter. Please try again.';
      addNotification({
        title: 'Could not duplicate newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      addNotification({
        title: 'Could not duplicate newsletter',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      duplicatingRef.current = false;
    }
  };

  const renderBody = () => {
    if (error && newsletters.length === 0) {
      return (
        <EmptyState titleText="Unable to load newsletters" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2" status="danger">
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<SyncAltIcon />} onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (newsletters.length === 0) {
      return isArchivedView ? (
        <EmptyState titleText="No archived newsletters" icon={ArchiveIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Sent newsletters you archive will appear here. Nothing has been archived yet.</EmptyStateBody>
        </EmptyState>
      ) : (
        <EmptyState titleText="No newsletters yet" icon={EnvelopeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Create your first newsletter from a template, fill in its content, then send a preview and queue it for the
            opted-in audience.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create newsletter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (filteredNewsletters.length === 0) {
      return (
        <EmptyState titleText="No newsletters match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term, or clear the search to see all newsletters.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearSearch}>
                Clear search
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Table aria-label="Newsletters list" variant="compact">
          <Thead>
            <Tr>
              <Th>Subject</Th>
              <Th>Status</Th>
              <Th>Size</Th>
              <Th>Created</Th>
              <Th>{isArchivedView ? 'Archived' : 'Last updated'}</Th>
              <Th screenReaderText="Row actions" />
            </Tr>
          </Thead>
          <Tbody>
            {paginatedNewsletters.map((newsletter) => (
              <Tr key={newsletter.id}>
                <Td dataLabel="Subject">{newsletter.subject}</Td>
                <Td dataLabel="Status">{renderStatus(newsletter)}</Td>
                <Td dataLabel="Size">{formatBytes(newsletter.html_bytes)}</Td>
                <Td dataLabel="Created">{formatDateNumeric(newsletter.created_at)}</Td>
                <Td dataLabel={isArchivedView ? 'Archived' : 'Last updated'}>
                  {formatDateNumeric(isArchivedView ? newsletter.archived_at : newsletter.updated_at)}
                </Td>
                <Td isActionCell>
                  <NewsletterRowActions
                    newsletter={newsletter}
                    onModify={setEditTarget}
                    onSend={setSendTarget}
                    onPreview={openPreview}
                    onDuplicate={(n) => void handleDuplicate(n)}
                    onDelete={setDeleteTarget}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Pagination
          itemCount={filteredNewsletters.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  // Full-panel spinner only when we have nothing to show: the initial load, and a view switch (which
  // clears the rows). A same-view refresh keeps its rows, so the table stays put.
  if (loading && newsletters.length === 0) {
    return <CenteredSpinner />;
  }

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by subject..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              id="newsletter-archived-filter"
              isOpen={isFilterOpen}
              selected={archivedFilter}
              onSelect={(_event, value) => {
                if (isArchivedFilter(value)) {
                  selectView(value);
                }
              }}
              onOpenChange={(open) => setIsFilterOpen(open)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsFilterOpen((open) => !open)}
                  isExpanded={isFilterOpen}
                  isDisabled={loading}
                  aria-label="Filter by archived status"
                >
                  {ARCHIVED_FILTER_LABEL[archivedFilter]}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value="active">Active</SelectOption>
                <SelectOption value="archived">Archived</SelectOption>
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="secondary" icon={<SyncAltIcon />} onClick={() => void refetch()} isLoading={loading} isDisabled={loading}>
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
              Create newsletter
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination itemCount={filteredNewsletters.length} perPage={perPage} page={page} onSetPage={onSetPage} onPerPageSelect={onPerPageSelect} />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {error && newsletters.length > 0 && (
        <Alert variant="danger" isInline title="Couldn't refresh newsletters" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}

      {renderBody()}

      {(isCreateOpen || editTarget !== null) && (
        <NewsletterEditorModal
          newsletter={editTarget}
          onClose={() => {
            setIsCreateOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleSaved}
        />
      )}
      {deleteTarget !== null && (
        <NewsletterDeleteModal newsletter={deleteTarget} onClose={() => setDeleteTarget(null)} onCompleted={removeNewsletter} />
      )}
      {sendTarget !== null && (
        <NewsletterSendModal
          newsletter={sendTarget}
          onClose={() => setSendTarget(null)}
          onSent={(updated) => {
            applyNewsletter(updated);
            setSendTarget(null);
          }}
        />
      )}
      {previewTarget !== null && previewAnchor !== null && (
        <Popover
          isVisible
          shouldClose={closePreview}
          triggerRef={() => previewAnchor}
          headerContent="Send a preview"
          aria-label="Send newsletter preview"
          bodyContent={<NewsletterPreviewPopoverBody newsletter={previewTarget} onDone={closePreview} />}
        />
      )}
    </>
  );
}

export default NewslettersTab;
